import type { PlatformClient } from "../PlatformClient";

export interface Department {
    id: string;
    clientID: string;
    name: string;
    alertConfig: {
        emailAddress: string | null;
        emergencyPhoneNumber: string | null;
    } | null;
    receiveEmailNotifications: boolean;
    archived: boolean;
    userCount?: number;
    guardianCount?: number;
    hasSirenBroadcastConfig?: boolean;
    hasSirenDeliveryMethods?: boolean;
    hasSirenEmailBranding?: boolean;
    hasSirenPhoneCallConfig?: boolean;
    hasSirenSMSConfig?: boolean;
}

export type DepartmentHydrate =
    | "broadcastConfigExistence"
    | "deliveryMethodsExistence"
    | "emailBrandingExistence"
    | "phoneCallConfigExistence"
    | "smsConfigExistence"
    | "userCount"
    | "guardianCount";

export interface ListDepartmentsParams {
    clientID: string;
    page?: number;
    limit?: number;
    search?: string;
    hydrate?: DepartmentHydrate[];
    archived?: boolean;
}

export interface ListDepartmentsResponse {
    items: Department[];
    total: number;
    totalPages: number;
    page: number;
}

export interface CreateDepartmentBody {
    name: string;
    email?: string;
    phone?: string;
    clientID?: string;
    receiveEmailNotifications?: boolean;
}

export interface UpdateDepartmentBody {
    name: string;
    email?: string;
    phone?: string;
    receiveEmailNotifications?: boolean;
}

export interface UserCountResponse {
    departmentID: string;
    total: number;
}

export interface Guardian {
    id: string;
    name: string;
    email: string;
}

export interface InfoFile {
    id: string;
    label: string;
    mimeType?: string;
    sizeBytes?: number;
}

export interface InfoConfig {
    information: string;
    files: InfoFile[];
}

export interface UpdateInfoConfigBody {
    information: string | null;
}

export function createDepartmentApi(client: PlatformClient) {
    return {
        list(params: ListDepartmentsParams) {
            const { hydrate, ...rest } = params;
            return client.get<ListDepartmentsResponse>("api/departments", {
                ...rest,
                ...(hydrate?.length ? { hydrate: hydrate.join(",") } : {}),
            });
        },

        get(id: string) {
            return client.get<Department>(`api/departments/${id}`);
        },

        create(body: CreateDepartmentBody) {
            return client.post<Department>("api/departments", body);
        },

        update(id: string, body: UpdateDepartmentBody) {
            return client.patch<void>(`api/departments/${id}`, body);
        },

        archive(id: string) {
            return client.post<void>(`api/departments/${id}/archive`);
        },

        unarchive(id: string) {
            return client.post<void>(`api/departments/${id}/unarchive`);
        },

        delete(id: string) {
            return client.delete<void>(`api/departments/${id}`);
        },

        userCount(id: string) {
            return client.get<UserCountResponse>(`api/departments/${id}/user-count`);
        },

        listGuardians(id: string) {
            return client.get<Guardian[]>(`api/departments/${id}/guardians`);
        },

        addGuardian(id: string, userID: string) {
            return client.post<void>(`api/departments/${id}/add-guardian`, { userID });
        },

        removeGuardian(id: string, userID: string) {
            return client.delete<void>(`api/departments/${id}/remove-guardian`, { userID });
        },

        unassignUsers(id: string) {
            return client.post<void>(`api/departments/${id}/unassign-users`);
        },

        getInfoConfig(id: string) {
            return client.get<InfoConfig>(`api/departments/${id}/info-config`);
        },

        updateInfoConfig(id: string, body: UpdateInfoConfigBody) {
            return client.patch<InfoConfig>(`api/departments/${id}/info-config`, body);
        },

        uploadInfoConfigFile(id: string, file: File, label?: string) {
            const form = new FormData();
            form.append("file", file);
            if (label) form.append("label", label);
            return client.upload<InfoConfig>(`api/departments/${id}/info-config/files`, form);
        },

        deleteInfoConfigFile(id: string, fileID: string) {
            return client.delete<void>(`api/departments/${id}/info-config/files/${fileID}`);
        },
    };
}

export type DepartmentApi = ReturnType<typeof createDepartmentApi>;
